import clsx from "clsx";
import { useFormik } from 'formik';
import { useEffect } from "react";
import { Modal } from 'react-bootstrap-v5';
import MaskedInput from "react-text-mask";
import * as Yup from 'yup';
import { useAuth } from "../../contexts/AuthProvider";
import { IClientes } from "../../interfaces/IClientes";
import { IUsuarios } from "../../interfaces/IUsuarios";
import UsuariosService from "../../services/UsuariosService";
import { MASCARA_CPF, MASCARA_TELEFONE } from "../../utils/Constants";

/*Tradução */
import '../../../i18n/config';
import { useTranslation } from 'react-i18next';

interface UsuariosModalProps {
    toggleModal: (loadItens?: boolean) => void,
    show: boolean,
    usuario?: IUsuarios,
    title: string,
    clientes: IClientes[] | undefined
}

function UsuariosModal({ toggleModal, show, usuario, title, clientes = [] }: UsuariosModalProps) {
    const { usuario: usuarioLogado } = useAuth();
    const { t } = useTranslation();

    const obrigacoesSchema = Yup.object().shape({
        nome: Yup.string().max(50, 'Maximo 50 caracteres').required('Descrição é obrigatório'),
        email: Yup.string().max(50, 'Maximo 50 caracteres').email('Esse e-mail não é válido').required('Descrição é obrigatório'),
        cpf: Yup.string().max(50, 'Maximo 50 caracteres').required('Descrição é obrigatório'),
        telefone: Yup.string().max(50, 'Maximo 50 caracteres'),
        celular: Yup.string().max(50, 'Maximo 50 caracteres').required('Descrição é obrigatório'),
        registroProfissional: Yup.string().nullable().max(50, 'Maximo 50 caracteres'),
        nomeUsuario: Yup.string().max(50, 'Maximo 50 caracteres').required('Descrição é obrigatório'),
        responsavelContabil: Yup.boolean(),
        perfil: Yup.string().max(50, 'Maximo 50 caracteres').required('Descrição é obrigatório'),
        clienteID: Yup.number().test('cliente-obrigatorio', 'Cliente é obrigatório', value => {
            if (formik.values.perfil !== 'Administrador' && formik.values.perfil !== 'Analista') {
                return true;
            }

            if (!value) {
                return false;
            }

            return true;
        }),
    })
    const formInitialValues: IUsuarios = {
        id: 0,
        nome: '',
        email: '',
        cpf: '',
        telefone: '',
        celular: '',
        registroProfissional: '',
        nomeUsuario: '',
        perfil: '',
        bloqueado: false,
        clienteID: undefined,
        responsavelContabil: false,
        adminMaestroContabil: false
    };

    const formik = useFormik({
        initialValues: formInitialValues,
        validationSchema: obrigacoesSchema,
        onSubmit: async (values, { setSubmitting }) => {
            if (values.id === 0) {
                await UsuariosService.criar(values, () => toggleModal(true), t('Usuário foi adicionado com sucesso'));
            }
            else {
                await UsuariosService.atualizar(values, () => toggleModal(true), t('Usuário foi editado com sucesso'));
            }

            setSubmitting(false);
        }
    });

    const perfilMaster = [
        { id: '1', perfil: 'Master' },
        { id: '2', perfil: 'Administrador' },
        { id: '3', perfil: 'Analista' }
    ];

    useEffect(() => {
        formik.resetForm();

        if (usuario) {
            formik.setValues(usuario);

            !usuario.nome && formik.setFieldTouched('nome', false);
            !usuario.email && formik.setFieldTouched('email', false);
            !usuario.cpf && formik.setFieldTouched('cpf', false);
            !usuario.telefone && formik.setFieldTouched('telefone', false);
            !usuario.celular && formik.setFieldTouched('celular', false);
            !usuario.nomeUsuario && formik.setFieldTouched('nomeUsuario', false);
            !usuario.perfil && formik.setFieldTouched('perfil', false);
            !usuario.clienteID && formik.setFieldTouched('clienteID', false);
        }

        // eslint-disable-next-line
    }, [show]);

    return <Modal size='lg' centered={true} show={show} onHide={toggleModal}>
        <div className="modal-content">
            <div className="modal-header">
                <h5 className="modal-title titulo-modal">{title}</h5>
                <div className="btn btn-icon btn-sm btn-active-light-primary ms-2">
                    <i onClick={() => toggleModal()} className="fas fa-times" style={{ color: '#5E6397' }} />
                </div>
            </div>
            <div className="modal-body">
                <form className="row g-3">
                    <div className="col-md-12">
                        <label htmlFor="form-nome" className="form-label required">{t("Nome")}</label>
                        <input {...formik.getFieldProps('nome')} placeholder={t("Nome Completo")} type="text" className={clsx(
                            'form-control',
                            {
                                'is-invalid': formik.touched.nome && formik.errors.nome
                            },
                            {
                                'is-valid': formik.touched.nome && !formik.errors.nome
                            }
                        )} id="form-nome" />
                    </div>
                    <div className="col-md-8">
                        <label htmlFor="form-email" className="form-label required">E-mail</label>
                        <input {...formik.getFieldProps('email')} placeholder={t("exemplo@email.com")} type="text" className={clsx(
                            'form-control',
                            {
                                'is-invalid': formik.touched.email && formik.errors.email
                            },
                            {
                                'is-valid': formik.touched.email && !formik.errors.email
                            }
                        )} id="form-email" />
                    </div>
                    <div className="col-md-4">
                        <label htmlFor="form-cpf" className="form-label required">{t('CPF')}</label>
                        <MaskedInput mask={MASCARA_CPF} {...formik.getFieldProps('cpf')} placeholder="000.000.000-00" type="text" className={clsx(
                            'form-control',
                            {
                                'is-invalid': formik.touched.cpf && formik.errors.cpf
                            },
                            {
                                'is-valid': formik.touched.cpf && !formik.errors.cpf
                            }
                        )} id="form-cpf" />
                    </div>
                    <div className="col-md-6">
                        <label htmlFor="form-telefone" className="form-label mt-5">{t("Telefone")}</label>
                        <MaskedInput mask={MASCARA_TELEFONE} {...formik.getFieldProps('telefone')} placeholder="(00)0000-0000" type="text" className={clsx(
                            'form-control',
                            {
                                'is-invalid': formik.touched.telefone && formik.errors.telefone
                            },
                            {
                                'is-valid': formik.touched.telefone && !formik.errors.telefone
                            }
                        )} id="form-telefone" />
                    </div>
                    <div className="col-md-6">
                        <label htmlFor="form-celular" className="form-label required mt-5">{t("Celular")}</label>
                        <MaskedInput mask={MASCARA_TELEFONE} {...formik.getFieldProps('celular')} placeholder="(00)0000-0000" type="text" className={clsx(
                            'form-control',
                            {
                                'is-invalid': formik.touched.celular && formik.errors.celular
                            },
                            {
                                'is-valid': formik.touched.celular && !formik.errors.celular
                            }
                        )} id="form-celular" />
                    </div>
                    <div className="col-md-12">
                        <label htmlFor="form-registro-profissional" className="form-label mt-5">{t("Registro Profissional")}</label>
                        <input {...formik.getFieldProps('registroProfissional')} value={formik.values.registroProfissional ?? ''} maxLength={50} placeholder="CRC-SP 000000/O-0" type="text" className={clsx(
                            'form-control',
                            {
                                'is-invalid': formik.touched.registroProfissional && formik.errors.registroProfissional
                            }
                        )} id="form-registro-profissional" />
                    </div>
                    <div className="col-md-8">
                        <label htmlFor="form-usuario" className="form-label required mt-5">{t("Nome de Usuário")}</label>
                        <input {...formik.getFieldProps('nomeUsuario')} placeholder={t("Digite algo")} type="text" className={clsx(
                            'form-control',
                            {
                                'is-invalid': formik.touched.nomeUsuario && formik.errors.nomeUsuario
                            },
                            {
                                'is-valid': formik.touched.nomeUsuario && !formik.errors.nomeUsuario
                            }
                        )} id="form-usuario" />
                    </div>
                    <div className="col-md-4" style={{ width: '100%', flexDirection: 'column', alignContent: 'center', justifyContent: 'center' }}>
                        <label className='form-check-label fw-bold'>
                            {t("Responsável Contabil")}
                        </label>
                        <input
                            {...formik.getFieldProps('responsavelContabil')}
                            checked={formik.values['responsavelContabil']}
                            style={{ marginLeft: '15px' }}
                            className='form-check-input'
                            type='checkbox'
                        />
                    </div>
                    <div className="col-md-4">
                        <label htmlFor="form-perfil" className="form-label required mt-5">{t("Perfil")}</label>
                        <select id="form-perfil"
                            {...formik.getFieldProps('perfil')}
                            onChange={async e => {
                                await formik.setFieldValue(e.target.name, e.target.value);
                                await formik.validateField('clienteID');
                            }} className="form-select">

                            <option value="" hidden>{t("Selecione")}</option>
                            {
                                perfilMaster.length > 0 && perfilMaster.map((x, index) => <option key={index} value={x.perfil}>{x.perfil}</option>)
                            }
                        </select>
                    </div>
                    {
                        usuarioLogado?.perfil === 'Master' && (formik.values.perfil === 'Administrador' || formik.values.perfil === 'Analista') && (
                            <div className="col-md-12">
                                <label htmlFor="form-perfil" className="form-label required mt-5">{t("Cliente")}</label>
                                <select id="form-cliente"
                                    {...formik.getFieldProps('clienteID')}
                                    onChange={async e => await formik.setFieldValue(e.target.name, e.target.value)} className="form-select">

                                    <option value="" hidden>{t("Selecione")}</option>
                                    {
                                        clientes.length > 0 && clientes.sort((x, y) => {
                                            if (!x.razaoSocial || !y.razaoSocial) {
                                                return 0
                                            }

                                            if (x.razaoSocial > y.razaoSocial) {
                                                return 1;
                                            }
                                            if (y.razaoSocial > x.razaoSocial) {
                                                return -1;
                                            }

                                            return 0;
                                        }).map((x, index) => <option key={index} value={x.id}>{x.razaoSocial}</option>)
                                    }
                                </select>
                            </div>
                        )
                    }
                </form>
            </div>
            <div className="modal-footer" style={{ margin: '0 auto' }}>
                <button disabled={formik.isSubmitting} onClick={() => toggleModal()} type="button" className="btn btn-modal-cancelar me-5"> {t("CANCELAR")} </button>
                <button disabled={formik.isSubmitting || !formik.isValid} onClick={() => formik.submitForm()} type="button" className="btn btn-primario ms-5">
                    {
                        !formik.isSubmitting && (usuario?.id ? t('EDITAR') : t('ADICIONAR'))
                    }
                    {
                        formik.isSubmitting &&
                        <span className='indicator-progress' style={{ display: 'block' }}>
                            {t("Salvando...")}
                            <span className='spinner-border spinner-border-sm align-middle ms-2' />
                        </span>
                    }
                </button>
            </div>
        </div>
    </Modal>
}

export default UsuariosModal;